/**
 * TemplateLinkedProductsWidget — Conditional-render linked products section.
 *
 * Hidden for new templates (no name) and when no products are linked.
 * When products exist: collapsible card, collapsed by default, with clickable product rows.
 *
 * Plan: 126-template-detail-page, Epic 03
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Package, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLinkedProducts } from "../api/customizationAdminHooks";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";

const i18n = {
  productsCount: {
    en: (n: number) => `${n} product${n !== 1 ? "s" : ""} use${n === 1 ? "s" : ""} this template`,
    th: (n: number) => `${n} สินค้าใช้แม่แบบนี้`,
  },
} as const;

export function TemplateLinkedProductsWidget({
  formData,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const docName = formData.name as string | undefined;

  const { data, isLoading } = useLinkedProducts(docName || "");

  const [expanded, setExpanded] = useState(false);

  if (!docName) return null;
  if (isLoading) return null;

  const products = data?.products || [];
  if (products.length === 0) return null;

  const countText = i18n.productsCount[language](products.length);

  return (
    <div>
      <SectionHeader label={getSectionTitleTranslation("Linked Products", language)} icon={ShoppingBag} color="indigo" />
      <div className="rounded-2xl overflow-hidden bg-card shadow-apple p-6">
        {/* Collapsible header */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center justify-between text-left"
          aria-expanded={expanded}
        >
          <span className="text-sm font-medium text-foreground">
            {countText}
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
          />
        </button>

        {/* Collapsible content */}
        {expanded && (
          <div className="mt-4">
            <div className="space-y-1">
              {products.map((product) => (
                <button
                  key={product.name}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-sm border bg-card p-3 text-left transition-colors hover:bg-accent/50"
                  onClick={() =>
                    navigate(
                      `/inventory/item/${encodeURIComponent(product.item_code)}`,
                    )
                  }
                >
                  <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {product.item_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {product.item_code}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
